import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  alternarAtivoModelo,
  atualizarDadosModelo,
  criarModelo,
  fetchCatalogoVariaveis,
  listarModelos,
  previewModelo,
  publicarVersaoModelo,
} from './actions';
import {
  type CategoriaModelo,
  type ModeloImpacto,
  type ModeloRenderResult,
  type ModeloSummary,
  type ModeloVariavel,
  type ModelosFiltersState,
  type ModelosRequestStatus,
} from '../types/modelosDocumentoTypes';

interface ModelosDocumentoState {
  items: ModeloSummary[];
  total: number;
  status: ModelosRequestStatus;
  error: string | null;
  filters: ModelosFiltersState;
  saving: boolean;
  savingError: string | null;
  preview: ModeloRenderResult | null;
  previewStatus: ModelosRequestStatus;
  previewError: string | null;
  previewRequestId: string | null;
  arquivamentoModelo: { id: string; nome: string } | null;
  arquivamentoImpacto: ModeloImpacto | null;
  arquivando: boolean;
  catalogoVariaveis: ModeloVariavel[];
}

const initialState: ModelosDocumentoState = {
  items: [],
  total: 0,
  status: 'idle',
  error: null,
  filters: {
    categoria: null,
    nome: '',
    apenasAtivos: false,
    page: 1,
  },
  saving: false,
  savingError: null,
  preview: null,
  previewStatus: 'idle',
  previewError: null,
  previewRequestId: null,
  arquivamentoModelo: null,
  arquivamentoImpacto: null,
  arquivando: false,
  catalogoVariaveis: [],
};

const modelosDocumentoSlice = createSlice({
  name: 'modelosDocumento',
  initialState,
  reducers: {
    setFiltroCategoria(state, action: PayloadAction<CategoriaModelo | null>) {
      state.filters.categoria = action.payload;
      state.filters.page = 1;
    },
    setFiltroNome(state, action: PayloadAction<string>) {
      state.filters.nome = action.payload;
      state.filters.page = 1;
    },
    setApenasAtivos(state, action: PayloadAction<boolean>) {
      state.filters.apenasAtivos = action.payload;
      state.filters.page = 1;
    },
    setPagina(state, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    resetFiltros(state) {
      state.filters = initialState.filters;
    },
    limparPreview(state) {
      state.preview = null;
      state.previewStatus = 'idle';
      state.previewError = null;
      state.previewRequestId = null;
    },
    limparSavingError(state) {
      state.savingError = null;
    },
    abrirConfirmacaoArquivamento(
      state,
      action: PayloadAction<{ modelo: { id: string; nome: string }; impacto: ModeloImpacto }>,
    ) {
      state.arquivamentoModelo = action.payload.modelo;
      state.arquivamentoImpacto = action.payload.impacto;
    },
    fecharConfirmacaoArquivamento(state) {
      state.arquivamentoModelo = null;
      state.arquivamentoImpacto = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarModelos.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(listarModelos.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(listarModelos.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Erro ao carregar os modelos';
      })
      .addCase(alternarAtivoModelo.pending, (state, action) => {
        state.arquivando = true;
        const index = state.items.findIndex((item) => item.id === action.meta.arg.id);
        if (index >= 0) {
          state.items[index] = { ...state.items[index], ativo: action.meta.arg.ativo };
        }
      })
      .addCase(alternarAtivoModelo.fulfilled, (state, action) => {
        state.arquivando = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) {
          state.items[index] = action.payload;
        }
      })
      .addCase(alternarAtivoModelo.rejected, (state, action) => {
        state.arquivando = false;
        const index = state.items.findIndex((item) => item.id === action.meta.arg.id);
        if (index >= 0) {
          state.items[index] = { ...state.items[index], ativo: !action.meta.arg.ativo };
        }
      })
      .addCase(criarModelo.pending, (state) => {
        state.saving = true;
        state.savingError = null;
      })
      .addCase(criarModelo.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(criarModelo.rejected, (state, action) => {
        state.saving = false;
        state.savingError = action.payload ?? 'Erro ao criar o modelo';
      })
      .addCase(publicarVersaoModelo.pending, (state) => {
        state.saving = true;
        state.savingError = null;
      })
      .addCase(publicarVersaoModelo.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(publicarVersaoModelo.rejected, (state, action) => {
        state.saving = false;
        state.savingError = action.payload ?? 'Erro ao publicar a nova versão';
      })
      .addCase(atualizarDadosModelo.pending, (state) => {
        state.saving = true;
        state.savingError = null;
      })
      .addCase(atualizarDadosModelo.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(atualizarDadosModelo.rejected, (state, action) => {
        state.saving = false;
        state.savingError = action.payload ?? 'Erro ao atualizar o nome/categoria do modelo';
      })
      .addCase(previewModelo.pending, (state, action) => {
        state.previewStatus = 'loading';
        state.previewError = null;
        state.previewRequestId = action.meta.requestId;
      })
      .addCase(previewModelo.fulfilled, (state, action) => {
        if (state.previewRequestId !== action.meta.requestId) return;
        state.previewStatus = 'succeeded';
        state.preview = action.payload;
      })
      .addCase(previewModelo.rejected, (state, action) => {
        if (state.previewRequestId !== action.meta.requestId) return;
        state.previewStatus = 'failed';
        state.previewError = action.payload ?? 'Erro ao gerar a pré-visualização';
      })
      .addCase(fetchCatalogoVariaveis.fulfilled, (state, action) => {
        state.catalogoVariaveis = action.payload;
      });
  },
});

export const {
  setFiltroCategoria,
  setFiltroNome,
  setApenasAtivos,
  setPagina,
  resetFiltros,
  limparPreview,
  limparSavingError,
  abrirConfirmacaoArquivamento,
  fecharConfirmacaoArquivamento,
} = modelosDocumentoSlice.actions;

export const modelosDocumentoReducer = modelosDocumentoSlice.reducer;
